import { useMemo } from "react";
import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePackagePrice } from "@tenant/packages";
import { useGetPriceDisplay } from "@tenant/ui";
import type { HotelShopBookingSectionProps } from "./HotelShopBookingSection";
import { PackageHotelShopSelectors } from "@/store/packages";

export const usePackageHotelShopBookingSection = ({
  hotel,
  goToNextStep,
  rateId: providedRateId,
}: HotelShopBookingSectionProps) => {
  const { t } = useI18nContext();
  const { getPriceDisplay } = useGetPriceDisplay();

  const { data: roomProduct, isLoading } = useSelector(
    HotelsShopSelectors.getCheapestRoomProduct
  );

  const { isLoading: isRoomsLoading } = useSelector(
    HotelsShopSelectors.getRooms
  );

  const packagesByRateId = useSelector(
    PackageHotelShopSelectors.getPackagesByRateId
  );

  // If rateId is not provided, use the cheapest room product's rateId
  const rateId = providedRateId ?? roomProduct?.rateId?.value;

  const packagePrice = packagesByRateId?.[rateId]?.price;

  const {
    context: { showPointsPricing, hasTenantSpecific, text, packageType },
  } = usePackagePrice(packagePrice);

  const totalPackageSavings = useMemo(() => {
    if (!packagePrice?.packageSavings) return null;

    const { priceText } = getPriceDisplay({
      prices: packagePrice.packageSavings,
    });

    return t("core-packages.priceSummary.packageSavings", {
      price: priceText,
    });
  }, [packagePrice?.packageSavings, getPriceDisplay, t]);

  const isAvailable = useMemo(() => {
    return (
      !isLoading &&
      roomProduct != null &&
      packagesByRateId?.[rateId]?.price != null
    );
  }, [isLoading, roomProduct, packagesByRateId, rateId]);

  const roomCTA = t("core-hotels.shop.price.roomCTA");

  return useMemo(() => {
    if (isLoading) {
      return {
        isLoading,
      };
    }

    if (!isAvailable) {
      return {
        isLoading: false,
        isAvailable: false,
        hotel,
        nextStep: null,
        text: {
          roomCTA: t("core-hotels.shop.price.roomCTA"),
          ...text,
        },
        unavailable: t("core-hotels.shop.price.unavailable"),
        showPointsPricing: false,
        hasTenantSpecific: false,
        isShopSearchVisible: false,
        packageType,
        totalPackageSavings,
      };
    }

    return {
      isLoading: false,
      isAvailable,
      hotel,
      nextStep: isRoomsLoading || !isAvailable ? null : goToNextStep,
      text: {
        ...text,
        roomCTA: isRoomsLoading ? t("loading") : roomCTA,
      },
      unavailable: t("core-hotels.shop.price.unavailable"),
      showPointsPricing,
      hasTenantSpecific,
      isShopSearchVisible: false, // Always hide search in packages flow
      packageType,
      totalPackageSavings,
    };
  }, [
    isLoading,
    isAvailable,
    hotel,
    isRoomsLoading,
    goToNextStep,
    text,
    t,
    roomCTA,
    showPointsPricing,
    hasTenantSpecific,
    packageType,
    totalPackageSavings,
  ]);
};
